import React from "react";
import { CheckCircle, ArrowRight, Clock, FileCheck, Wallet, Shield, Users, Bell, GraduationCap } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal, MagneticButton } from "../components/shared/kursusUi";

const PartnerPage = () => {

  const benefits = [
    { icon: Clock, title: "Huskeservice", description: "Slip for bekymringer, vi overvåger dine chaufførers certifikater og sender kursusforslag i god tid." },
    { icon: FileCheck, title: "Tilmelding", description: "Vi klarer alt det praktiske, når dine medarbejdere skal på AMU-kurser, også den lovpligtige efteruddannelse." },
    { icon: Wallet, title: "Tilskud", description: "Få mest muligt ud af dine midler, vi hjælper med refusioner og tilskud som VEU, kørselsgodtgørelse og kompetencefonde." },
    { icon: Shield, title: "Sikkerhedsrådgiver", description: "Styrk din sikkerhed, vi stiller en rådgiver til rådighed, der sikrer lovkrav, uddannelser og årsrapport." },
    { icon: Users, title: "Rekruttering", description: "Spar tid og find de rette kandidater, vi hjælper med rekruttering af chauffører til din virksomhed." },
    { icon: GraduationCap, title: "Medarbejder", description: "Vi hjælper din virksomhed med at finde og rekruttere medarbejdere. Vi fungerer som et eksternt HR-bureau og hjælper med AUB-refusion." },
    { icon: Bell, title: "Nyhedsbreve", description: "Få de nyeste branchetrends og lovgivningsændringer direkte i din indbakke." }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal>
              <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-5 text-white" style={{ backgroundColor: PRIMARY }}>
                Gratis partnerprogram
              </span>
            </Reveal>
            <Reveal i={1}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] mb-6">
                Votra Partner
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-4 max-w-2xl">
                Slip for bøvlet med en Votra Partneraftale. Som partner får din virksomhed fuld support til efteruddannelse og opkvalificering af medarbejdere, helt uden ekstra omkostninger.
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="text-slate-400 mb-8 max-w-2xl">
                Det er gratis at blive partner. Vi tager os af alt det administrative, så du kan fokusere på det, der virkelig tæller: din kerneforretning.
              </p>
            </Reveal>
            <Reveal i={4}>
              <MagneticButton
                to="/kontakt"
                className="kursus-glow-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white no-underline"
                style={{ backgroundColor: PRIMARY }}
                data-testid="partner-hero-cta"
              >
                Bliv kontaktet <ArrowRight className="h-5 w-5" />
              </MagneticButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <div className="flex justify-center"><Overline>Fordele</Overline></div>
            <h2 className="font-display text-3xl md:text-4xl font-medium mb-4 text-slate-900 leading-tight">
              Som Votra Partner får du:
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Bag dig står et stærkt Votra-team klar til at hjælpe og servicere din virksomhed. Vi håndterer alt det praktiske for dig, enkelt, professionelt og uden merpris.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={idx}
                  className="votra-rise bg-white rounded-2xl border border-slate-200/80 p-6 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,40,90,0.14)]"
                  style={{ animationDelay: `${Math.min(idx, 5) * 70}ms` }}
                  data-testid={`partner-benefit-${idx}`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'hsl(210, 100%, 45%, 0.1)' }}>
                    <IconComponent className="h-6 w-6" style={{ color: PRIMARY }} />
                  </div>
                  <h3 className="font-semibold mb-2 text-slate-900">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Partner Portal Section */}
      <div className="bg-white py-16 md:py-20 border-y border-slate-100">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Reveal>
              <Overline>Partnerportal</Overline>
              <h2 className="font-display text-3xl md:text-4xl font-medium mb-4 text-slate-900 leading-tight">
                Vores partnerportal sætter dig i fuld kontrol
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                Slip for at bruge tid på at holde styr på certifikater, datoer og kursusoversigter i forskellige systemer. Vi har samlet det hele i vores partnerportal, et værktøj, der gør administrationen langt mere enkel og effektiv.
              </p>
            </Reveal>

            <Reveal className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-100">
              <h3 className="font-semibold mb-5 text-slate-900">Med partnerportalen kan du blandt andet:</h3>
              <ul className="space-y-3">
                {[
                  "Oprette chauffører og registrere alle certifikat-udløb",
                  "Give chauffører mulighed for selv at validere deres data",
                  "Oprette brugere og afdelinger med adgang kun til relevant data",
                  "Få et visuelt overblik over certifikater via visuelle grafer",
                  "Se ledige kurser, der matcher certifikaterne"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: PRIMARY }} />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <Reveal className="relative overflow-hidden rounded-3xl p-10 md:p-14 text-center text-white" style={{ backgroundColor: INK }}>
            <AuroraBlue />
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-4xl font-medium mb-4">Kom i gang</h2>
              <p className="text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Tag det første skridt mod en effektiv og problemfri efteruddannelse. Opret en Votra Partner aftale og få adgang til vores omfattende support og ekspertise. Det er helt gratis!
              </p>
              <MagneticButton
                to="/kontakt"
                className="kursus-glow-btn px-8 py-4 rounded-full font-semibold text-white no-underline"
                style={{ backgroundColor: PRIMARY }}
                data-testid="partner-cta-kontakt"
              >
                Kontakt os i dag
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnerPage;
